import { motion } from 'framer-motion';
import { Users, FileText, Sparkles } from 'lucide-react';
import { mockUsers, mockPapers, INSTITUTION_CONFIG, getImpactMetrics } from './mockData';

interface KPICardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  progress?: number; // 0-100
  delay: number;
}

function KPICard({ icon, label, value, sub, progress, delay }: KPICardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      className="relative overflow-hidden rounded-2xl border border-border/50 bg-card/80 backdrop-blur-xl p-6 shadow-lg"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          {icon}
        </div>
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
      </div>

      <p className="text-3xl font-bold text-foreground mb-1">{value}</p>
      <p className="text-sm text-muted-foreground">{sub}</p>

      {progress !== undefined && (
        <div className="mt-4">
          <div className="h-1.5 w-full rounded-full bg-muted">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 1.2, delay: delay + 0.3, ease: 'easeOut' }}
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70"
            />
          </div>
        </div>
      )}
    </motion.div>
  );
}

const KPICards = () => {
  const metrics = getImpactMetrics();
  const activeUsers = mockUsers.length;
  const totalPapers = mockPapers.length;
  const reviewsUsed = metrics.totalReviewsUsed;
  const totalReviews = INSTITUTION_CONFIG.totalReviews;
  const remaining = totalReviews - reviewsUsed;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <KPICard
        icon={<Users className="h-5 w-5 text-primary" />}
        label="Nutzer"
        value={`${activeUsers}/${INSTITUTION_CONFIG.maxUsers}`}
        sub="aktive Forscher"
        progress={(activeUsers / INSTITUTION_CONFIG.maxUsers) * 100}
        delay={0.1}
      />
      <KPICard
        icon={<FileText className="h-5 w-5 text-primary" />}
        label="Paper"
        value={`${totalPapers}`}
        sub="im Institut"
        delay={0.2}
      />
      <KPICard
        icon={<Sparkles className="h-5 w-5 text-primary" />}
        label="AI Reviews"
        value={`${reviewsUsed}/${totalReviews}`}
        sub={`${remaining} übrig`}
        progress={(reviewsUsed / totalReviews) * 100}
        delay={0.3}
      />
    </div>
  );
};

export default KPICards;
